// (c) 2026 Owl Consultations. All rights reserved.
// Proprietary and confidential — licensed use only. Unauthorized use,
// copying, or distribution is prohibited and may be prosecuted.

import type React from 'react'
import { formatUsTimestamp } from '../../api/format'

// State -> color (tone) mapping
const CLINICAL_TONES: Record<string, { bg: string; fg: string; label: string }> = {
  draft:             { bg: '#f3f4f6', fg: '#374151', label: 'Draft' },
  submitted:         { bg: '#fef3c7', fg: '#92400e', label: 'Submitted' },
  verified:          { bg: '#dbeafe', fg: '#1e40af', label: 'Verified' },
  ready_to_fill:     { bg: '#d1fae5', fg: '#065f46', label: 'Ready to fill' },
  dispensed:         { bg: '#e0e7ff', fg: '#3730a3', label: 'Dispensed' },
  out_for_delivery:  { bg: '#ede9fe', fg: '#5b21b6', label: 'Out for delivery' },
  delivered:         { bg: '#dcfce7', fg: '#15803d', label: 'Delivered' },
  on_hold:           { bg: '#fef3c7', fg: '#854d0e', label: 'On hold' },
  cancelled:         { bg: '#f3f4f6', fg: '#6b7280', label: 'Cancelled' },
  rejected:          { bg: '#fee2e2', fg: '#991b1b', label: 'Rejected' },
}

const PAYER_TONES: Record<string, { bg: string; fg: string; label: string }> = {
  not_required:        { bg: '#f3f4f6', fg: '#6b7280', label: 'No insurance' },
  awaiting_insurance:  { bg: '#fef3c7', fg: '#92400e', label: 'Awaiting ins.' },
  pa_required:         { bg: '#fed7aa', fg: '#9a3412', label: 'PA needed' },
  pa_pending:          { bg: '#fde68a', fg: '#92400e', label: 'PA pending' },
  pa_approved:         { bg: '#d1fae5', fg: '#065f46', label: 'PA approved' },
  pa_denied:           { bg: '#fecaca', fg: '#991b1b', label: 'PA denied' },
  insurance_rejected:  { bg: '#fecaca', fg: '#991b1b', label: 'Ins. rejected' },
  cash_pay:            { bg: '#dbeafe', fg: '#1e40af', label: 'Cash pay' },
}

export function ClinicalStatePill({ state, label }: { state: string; label?: string }) {
  const t = CLINICAL_TONES[state] ?? { bg: '#f3f4f6', fg: '#374151', label: state }
  return <Pill bg={t.bg} fg={t.fg}>{label ?? t.label}</Pill>
}

export function PayerStatePill({ state }: { state: string }) {
  const t = PAYER_TONES[state] ?? { bg: '#f3f4f6', fg: '#374151', label: state }
  return <Pill bg={t.bg} fg={t.fg}>{t.label}</Pill>
}

function Pill({ bg, fg, children }: { bg: string; fg: string; children: React.ReactNode }) {
  return (
    <span style={{
      display: 'inline-block',
      padding: '2px 10px',
      borderRadius: 12,
      background: bg,
      color: fg,
      fontSize: 12,
      fontWeight: 500,
      lineHeight: '18px',
    }}>{children}</span>
  )
}

export function ErrorBanner({ message, onClose }: { message: string; onClose?: () => void }) {
  return (
    <div role="alert" style={{
      background: '#fee2e2', border: '1px solid #fca5a5', color: '#991b1b',
      padding: '10px 14px', borderRadius: 6, marginBottom: 12,
      fontSize: 14, display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 12,
    }}>
      <span>{message}</span>
      {onClose && (
        <button onClick={onClose} aria-label="dismiss"
          style={{ background: 'transparent', color: '#991b1b', fontWeight: 600, padding: '0 4px' }}>
          ×
        </button>
      )}
    </div>
  )
}

export function LoadingState({ label = 'Loading…' }: { label?: string }) {
  return (
    <div style={{ padding: 32, textAlign: 'center', color: 'var(--color-text-mute)', fontSize: 14 }}>
      {label}
    </div>
  )
}

export function EmptyState({ message }: { message: string }) {
  return (
    <div style={{ padding: 32, textAlign: 'center', color: 'var(--color-text-mute)', fontSize: 14 }}>
      {message}
    </div>
  )
}

export function formatTimestamp(s: string): string {
  return formatUsTimestamp(s)
}

export function relativeTime(s: string): string {
  try {
    const d = new Date(s).getTime()
    const now = Date.now()
    const diffSec = Math.round((now - d) / 1000)
    if (diffSec < 60)         return `${diffSec}s ago`
    if (diffSec < 3600)       return `${Math.round(diffSec / 60)}m ago`
    if (diffSec < 86400)      return `${Math.round(diffSec / 3600)}h ago`
    return `${Math.round(diffSec / 86400)}d ago`
  } catch { return s }
}
